import { GraftCodePrimitiveType } from '@graftcode/protocol'

export type CanonicalType =
  | 'string'
  | 'char'
  | 'boolean'
  | 'byte'
  | 'sbyte'
  | 'int16'
  | 'uint16'
  | 'int32'
  | 'uint32'
  | 'int64'
  | 'uint64'
  | 'float'
  | 'double'
  | 'decimal'
  | 'void'
  | 'object'
  | 'any'
  | 'date'
  | 'datetime'
  | 'guid'
  | 'list'
  | 'map'
  | 'set'

export const PRIMITIVE_TO_CANONICAL: Partial<Record<GraftCodePrimitiveType, CanonicalType>> = {
  [GraftCodePrimitiveType.STRING]: 'string',
  [GraftCodePrimitiveType.CHAR]: 'char',
  [GraftCodePrimitiveType.BOOLEAN]: 'boolean',
  [GraftCodePrimitiveType.BYTE]: 'byte',
  [GraftCodePrimitiveType.INTEGER]: 'int32',
  [GraftCodePrimitiveType.UNSIGNED_INTEGER]: 'uint32',
  [GraftCodePrimitiveType.LONG]: 'int64',
  [GraftCodePrimitiveType.UNSIGNED_LONG_LONG]: 'uint64',
  [GraftCodePrimitiveType.FLOAT]: 'float',
  [GraftCodePrimitiveType.DOUBLE]: 'double',
  [GraftCodePrimitiveType.VOID]: 'void',
  [GraftCodePrimitiveType.UNKNOWN]: 'any',
}

export const RAW_TO_CANONICAL: Record<string, CanonicalType> = {
  'System.String': 'string',
  String: 'string',
  string: 'string',
  'System.Char': 'char',
  Char: 'char',
  char: 'char',
  'System.Boolean': 'boolean',
  Boolean: 'boolean',
  bool: 'boolean',
  'System.Byte': 'byte',
  Byte: 'byte',
  byte: 'byte',
  'System.SByte': 'sbyte',
  SByte: 'sbyte',
  sbyte: 'sbyte',
  'System.Int16': 'int16',
  Int16: 'int16',
  short: 'int16',
  'System.UInt16': 'uint16',
  UInt16: 'uint16',
  ushort: 'uint16',
  'System.Int32': 'int32',
  Int32: 'int32',
  int: 'int32',
  'System.UInt32': 'uint32',
  UInt32: 'uint32',
  uint: 'uint32',
  'System.Int64': 'int64',
  Int64: 'int64',
  long: 'int64',
  'System.UInt64': 'uint64',
  UInt64: 'uint64',
  ulong: 'uint64',
  'System.Single': 'float',
  Single: 'float',
  float: 'float',
  'System.Double': 'double',
  Double: 'double',
  double: 'double',
  'System.Decimal': 'decimal',
  Decimal: 'decimal',
  decimal: 'decimal',
  'System.Void': 'void',
  Void: 'void',
  void: 'void',
  'System.Object': 'object',
  Object: 'object',
  object: 'object',
  'System.DateTime': 'datetime',
  DateTime: 'datetime',
  'System.DateOnly': 'date',
  DateOnly: 'date',
  'System.Guid': 'guid',
  Guid: 'guid',
  'System.Collections.Generic.List': 'list',
  'System.Collections.Generic.Dictionary': 'map',
  'System.Collections.Generic.HashSet': 'set',

  'java.lang.String': 'string',
  'java.lang.Character': 'char',
  Character: 'char',
  'java.lang.Boolean': 'boolean',
  'java.lang.Byte': 'byte',
  'java.lang.Short': 'int16',
  Short: 'int16',
  'java.lang.Integer': 'int32',
  Integer: 'int32',
  'java.lang.Long': 'int64',
  Long: 'int64',
  'java.lang.Float': 'float',
  'java.lang.Double': 'double',
  'java.lang.Object': 'object',
  'java.lang.Void': 'void',
  'java.math.BigDecimal': 'decimal',
  BigDecimal: 'decimal',
  'java.util.Date': 'datetime',
  'java.time.LocalDate': 'date',
  LocalDate: 'date',
  'java.time.LocalDateTime': 'datetime',
  LocalDateTime: 'datetime',
  'java.util.UUID': 'guid',
  UUID: 'guid',
  'java.util.List': 'list',
  List: 'list',
  'java.util.Map': 'map',
  Map: 'map',
  'java.util.Set': 'set',
  Set: 'set',

  number: 'double',
  bigint: 'int64',

  str: 'string',
  None: 'void',
  NoneType: 'void',
  list: 'list',
  dict: 'map',
  set: 'set',
  date: 'date',
  datetime: 'datetime',

  array: 'list',

  any: 'any',
  unknown: 'any',
}

type DisplayMap = Partial<Record<CanonicalType, string>>

export const TECHNOLOGY_TYPE_DISPLAY: Record<string, DisplayMap> = {
  npm: {
    string: 'string',
    char: 'string',
    boolean: 'boolean',
    byte: 'number',
    sbyte: 'number',
    int16: 'number',
    uint16: 'number',
    int32: 'number',
    uint32: 'number',
    int64: 'bigint',
    uint64: 'bigint',
    float: 'number',
    double: 'number',
    decimal: 'number',
    void: 'void',
    object: 'object',
    any: 'any',
    date: 'Date',
    datetime: 'Date',
    guid: 'string',
    list: 'Array',
    map: 'Map',
    set: 'Set',
  },
  nuget: {
    string: 'string',
    char: 'char',
    boolean: 'bool',
    byte: 'byte',
    sbyte: 'sbyte',
    int16: 'short',
    uint16: 'ushort',
    int32: 'int',
    uint32: 'uint',
    int64: 'long',
    uint64: 'ulong',
    float: 'float',
    double: 'double',
    decimal: 'decimal',
    void: 'void',
    object: 'object',
    any: 'object',
    date: 'DateOnly',
    datetime: 'DateTime',
    guid: 'Guid',
    list: 'List',
    map: 'Dictionary',
    set: 'HashSet',
  },
  maven: {
    string: 'String',
    char: 'char',
    boolean: 'boolean',
    byte: 'byte',
    sbyte: 'byte',
    int16: 'short',
    uint16: 'short',
    int32: 'int',
    uint32: 'int',
    int64: 'long',
    uint64: 'long',
    float: 'float',
    double: 'double',
    decimal: 'BigDecimal',
    void: 'void',
    object: 'Object',
    any: 'Object',
    date: 'LocalDate',
    datetime: 'LocalDateTime',
    guid: 'UUID',
    list: 'List',
    map: 'Map',
    set: 'Set',
  },
  pypi: {
    string: 'str',
    char: 'str',
    boolean: 'bool',
    byte: 'int',
    sbyte: 'int',
    int16: 'int',
    uint16: 'int',
    int32: 'int',
    uint32: 'int',
    int64: 'int',
    uint64: 'int',
    float: 'float',
    double: 'float',
    decimal: 'Decimal',
    void: 'None',
    object: 'object',
    any: 'any',
    date: 'date',
    datetime: 'datetime',
    guid: 'UUID',
    list: 'list',
    map: 'dict',
    set: 'set',
  },
  packagist: {
    string: 'string',
    char: 'string',
    boolean: 'bool',
    byte: 'int',
    sbyte: 'int',
    int16: 'int',
    uint16: 'int',
    int32: 'int',
    uint32: 'int',
    int64: 'int',
    uint64: 'int',
    float: 'float',
    double: 'float',
    decimal: 'float',
    void: 'void',
    object: 'object',
    any: 'object',
    date: 'DateTimeImmutable',
    datetime: 'DateTimeImmutable',
    guid: 'string',
    list: 'array',
    map: 'array',
    set: 'array',
  },
  ruby: {
    string: 'String',
    char: 'String',
    boolean: 'Boolean',
    byte: 'Integer',
    sbyte: 'Integer',
    int16: 'Integer',
    uint16: 'Integer',
    int32: 'Integer',
    uint32: 'Integer',
    int64: 'Integer',
    uint64: 'Integer',
    float: 'Float',
    double: 'Float',
    decimal: 'BigDecimal',
    void: 'nil',
    object: 'Object',
    any: 'any',
    date: 'Date',
    datetime: 'DateTime',
    guid: 'String',
    list: 'Array',
    map: 'Hash',
    set: 'Set',
  },
}

export function getTypeDisplay(canonical: CanonicalType, technology?: string): string {
  if (!technology) return canonical
  return TECHNOLOGY_TYPE_DISPLAY[technology]?.[canonical] ?? canonical
}

export function getCanonicalFromRawName(raw: string): CanonicalType | undefined {
  return RAW_TO_CANONICAL[raw]
}
